import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";
import { TextBlockContent, type TextBlockContentProps } from "./Text";
import { Image } from "@/components/ui/Image";
import { Video } from "@/components/ui/Video";
import { PhoneMockup, DesktopMockup, LaptopMockup } from "@/components/ui/device-mockups";
import type { MediaData, ThemeVariant } from "@/types";
const mediaTextVariants = cva("container grid grid-cols-12 md:gap-x-0 gap-y-8", {
  variants: {
    variant: {
      light: "",
      dark: ""
    }
  },
  defaultVariants: {
    variant: "light"
  }
});
const mediaContainerVariants = cva("flex flex-col justify-center", {
  variants: {
    mediaWidth: {
      half: "col-span-12 lg:col-span-6",
      third: "col-span-12 lg:col-span-4",
      twoThirds: "col-span-12 lg:col-span-8",
      fiveTwelfths: "col-span-12 lg:col-span-5"
    },
    mediaPosition: {
      left: "order-1",
      right: "order-1 lg:order-2"
    }
  },
  compoundVariants: [{
    mediaPosition: "right",
    mediaWidth: "half",
    className: "lg:col-start-7"
  }, {
    mediaPosition: "right",
    mediaWidth: "third",
    className: "lg:col-start-9"
  }, {
    mediaPosition: "right",
    mediaWidth: "twoThirds",
    className: "lg:col-start-5"
  }, {
    mediaPosition: "right",
    mediaWidth: "fiveTwelfths",
    className: "lg:col-start-8"
  }],
  defaultVariants: {
    mediaWidth: "half",
    mediaPosition: "left"
  }
});
const textContainerVariants = cva("flex flex-col justify-center", {
  variants: {
    mediaWidth: {
      half: "col-span-12 lg:col-span-5",
      third: "col-span-12 lg:col-span-7",
      twoThirds: "col-span-12 lg:col-span-3",
      fiveTwelfths: "col-span-12 lg:col-span-6"
    },
    mediaPosition: {
      left: "order-2",
      right: "order-2 lg:order-1 lg:col-start-1"
    }
  },
  compoundVariants: [{
    mediaPosition: "left",
    mediaWidth: "half",
    className: "lg:col-start-8"
  }, {
    mediaPosition: "left",
    mediaWidth: "third",
    className: "lg:col-start-6"
  }, {
    mediaPosition: "left",
    mediaWidth: "twoThirds",
    className: "lg:col-start-10"
  }, {
    mediaPosition: "left",
    mediaWidth: "fiveTwelfths",
    className: "lg:col-start-7"
  }],
  defaultVariants: {
    mediaWidth: "half",
    mediaPosition: "left"
  }
});
const mediaWrapperVariants = cva("relative w-full", {
  variants: {
    mediaShape: {
      auto: "",
      square: "aspect-square"
    }
  },
  defaultVariants: {
    mediaShape: "auto"
  }
});
export type MediaType = "image" | "video";
export type MediaPosition = "left" | "right";
export type MediaWidth = "half" | "third" | "twoThirds" | "fiveTwelfths";
export type MediaShape = "auto" | "square";
export type MockupType = "phone" | "desktop" | "laptop";
export interface MediaTextProps extends Omit<TextBlockContentProps, "arrowDirection"> {
  variant?: ThemeVariant;
  media: MediaData;
  mediaPosition?: MediaPosition;
  mediaWidth?: MediaWidth;
  mediaShape?: MediaShape;
  mockup?: MockupType;
}

/**
 * MediaText component - combines media (image/video) with text content
 * Supports flexible positioning and sizing with grid layout, including optional device mockups
 */
export const MediaText: React.FC<MediaTextProps> = t0 => {
  const $ = _c(38);
  const {
    media,
    mediaPosition: t1,
    mediaWidth: t2,
    mediaShape: t3,
    mockup,
    topline,
    headline,
    text,
    headingTag: t4,
    headingStyle: t5,
    variant: t6,
    button,
    className: t7
  } = t0;
  const mediaPosition = t1 === undefined ? "left" : t1;
  const mediaWidth = t2 === undefined ? "half" : t2;
  const mediaShape = t3 === undefined ? "auto" : t3;
  const headingTag = t4 === undefined ? "h2" : t4;
  const headingStyle = t5 === undefined ? "h2" : t5;
  const variant = t6 === undefined ? "light" : t6;
  const className = t7 === undefined ? "" : t7;
  let t8;
  if ($[0] !== button || $[1] !== variant) {
    t8 = button ? {
      ...button,
      color: button.color ?? (variant === "light" ? "yellow" : "purple")
    } : undefined;
    $[0] = button;
    $[1] = variant;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const buttonWithVariant = t8;
  let t9;
  if ($[3] !== media.image || $[4] !== media.type || $[5] !== media.video || $[6] !== mediaShape || $[7] !== mockup) {
    t9 = () => {
      let content = null;
      if (media.type === "image" && media.image) {
        content = <Image {...media.image} rounded={mockup ? undefined : "lg"} className="h-full w-full object-cover" />;
      } else {
        if (media.type === "video" && media.video) {
          content = <Video video={media.video} className={cn("h-full w-full object-cover", !mockup && "rounded-lg")} />;
        }
      }
      if (!content) {
        return null;
      }
      if (mockup) {
        if (mockup === "phone") {
          return <PhoneMockup>{content}</PhoneMockup>;
        }
        const MockupComponent = {
          phone: PhoneMockup,
          desktop: DesktopMockup,
          laptop: LaptopMockup
        }[mockup];
        return <MockupComponent className="[--base:300px] sm:[--base:400px] md:[--base:500px] lg:[--base:350px] xl:[--base:440px] 2xl:[--base:520px]">{content}</MockupComponent>;
      }
      return <div className={mediaWrapperVariants({
        mediaShape
      })}>{content}</div>;
    };
    $[3] = media.image;
    $[4] = media.type;
    $[5] = media.video;
    $[6] = mediaShape;
    $[7] = mockup;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const renderMedia = t9;
  let t10;
  if ($[9] !== className || $[10] !== variant) {
    t10 = mediaTextVariants({
      variant,
      className
    });
    $[9] = className;
    $[10] = variant;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== mediaPosition || $[13] !== mediaWidth) {
    t11 = mediaContainerVariants({
      mediaWidth,
      mediaPosition
    });
    $[12] = mediaPosition;
    $[13] = mediaWidth;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== renderMedia) {
    t12 = renderMedia();
    $[15] = renderMedia;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t11 || $[18] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== mediaPosition || $[21] !== mediaWidth) {
    t14 = textContainerVariants({
      mediaWidth,
      mediaPosition
    });
    $[20] = mediaPosition;
    $[21] = mediaWidth;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== buttonWithVariant || $[24] !== headingStyle || $[25] !== headingTag || $[26] !== headline || $[27] !== text || $[28] !== topline || $[29] !== variant) {
    t15 = <TextBlockContent topline={topline} headline={headline} text={text} headingTag={headingTag} headingStyle={headingStyle} variant={variant} button={buttonWithVariant} />;
    $[23] = buttonWithVariant;
    $[24] = headingStyle;
    $[25] = headingTag;
    $[26] = headline;
    $[27] = text;
    $[28] = topline;
    $[29] = variant;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <div className={t14}>{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t10 || $[35] !== t13 || $[36] !== t16) {
    t17 = <div className={t10}>{t13}{t16}</div>;
    $[34] = t10;
    $[35] = t13;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};